/**
 * Корневой компонент UI бэклога: вход, фильтры, доска, создание карточки.
 *
 * Flow: восстановление сессии из localStorage → загрузка карточек с фильтрами →
 *       доска → панель карточки по клику. Перетаскивание меняет статус на
 *       сервере и перезагружает список.
 *
 * Зависимости:
 *   - react: состояние и загрузка;
 *   - ./api: вход и операции бэклога;
 *   - ./Board, ./ItemPanel: доска и панель карточки;
 *   - ./model: справочники и локальная проверка перехода.
 */
import { useCallback, useEffect, useState } from 'react'
import Board from './Board.jsx'
import StageMap from './StageMap.jsx'
import Architecture from './Architecture.jsx'
import ItemPanel from './ItemPanel.jsx'
import { createItem, getMe, getToken, listItems, login, setSession, setStatus, listMilestones, moveItem, getProjectStatus, saveProjectStatus, getArchitecture } from './api.js'
import { COLUMNS, PRIORITIES, TYPES, transitionBlocker } from './model.js'

/** Роли, которым разрешено вести бэклог; остальные — только просмотр. */
const EDITOR_ROLES = ['admin', 'developer']

function LoginForm({ onSuccess }) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  async function submit(event) {
    event.preventDefault()
    setBusy(true)
    setError('')
    try {
      const data = await login(username, password)
      setSession(data.access_token)
      // Профиля в ответе входа нет — он приходит отдельным запросом.
      onSuccess(await getMe())
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="center">
      <form className="card-form" onSubmit={submit}>
        <h1>Бэклог</h1>
        <p className="muted">Разработка и техподдержка Kaznacheistvo</p>
        {error && <div className="error">{error}</div>}
        <label htmlFor="u">Логин</label>
        <input id="u" value={username} autoComplete="username" onChange={(e) => setUsername(e.target.value)} />
        <label htmlFor="p">Пароль</label>
        <input id="p" type="password" value={password} autoComplete="current-password"
               onChange={(e) => setPassword(e.target.value)} />
        <div style={{ marginTop: 16 }}>
          <button className="btn" style={{ width: '100%' }} type="submit"
                  disabled={busy || !username || !password}>
            {busy ? 'Проверяем…' : 'Войти'}
          </button>
        </div>
      </form>
    </div>
  )
}

function CreateForm({ onCreated, onCancel }) {
  const [form, setForm] = useState({
    title: '', type: 'feature', priority: 'P2', value: '', acceptance: '',
  })
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  async function submit(event) {
    event.preventDefault()
    setBusy(true)
    setError('')
    try {
      // Критерии вводятся построчно: каждая непустая строка — отдельный
      // критерий. Карточка без них не карточка (правило 06), поэтому поле
      // обязательное и в форме.
      const acceptance = form.acceptance.split('\n').map((s) => s.trim()).filter(Boolean)
      await createItem({ ...form, acceptance })
      onCreated()
    } catch (e) {
      setError(e.message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="overlay" onClick={onCancel}>
      <form className="panel" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <h2>Новая карточка</h2>
        {error && <div className="error">{error}</div>}
        <label htmlFor="t">Название — что меняется с точки зрения заявителя</label>
        <input id="t" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        <div className="row">
          <div style={{ flex: 1 }}>
            <label htmlFor="ty">Тип</label>
            <select id="ty" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              {TYPES.map((t) => <option key={t.key} value={t.key}>{t.title}</option>)}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label htmlFor="pr">Приоритет</label>
            <select id="pr" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
              {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>
        <label htmlFor="v">Ценность — зачем это заявителю, одно предложение</label>
        <input id="v" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
        <label htmlFor="a">Критерии приёмки — по одному в строке (Дано / Когда / Тогда)</label>
        <textarea id="a" value={form.acceptance}
                  onChange={(e) => setForm({ ...form, acceptance: e.target.value })} />
        <div className="row" style={{ marginTop: 14 }}>
          <button className="btn" type="submit" disabled={busy || !form.title.trim()}>Создать</button>
          <button className="btn ghost" type="button" onClick={onCancel}>Отмена</button>
        </div>
      </form>
    </div>
  )
}

export default function App() {
  const [user, setUser] = useState(null)
  // Пока профиль по сохранённому токену не пришёл, показывать форму входа
  // нельзя: вошедший человек увидел бы её на каждом обновлении страницы.
  const [booting, setBooting] = useState(Boolean(getToken()))
  const [items, setItems] = useState([])
  const [filters, setFilters] = useState({ type: '', assignee: '', query: '', archived: false })
  const [openId, setOpenId] = useState(null)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [view, setView] = useState('map')
  const [milestones, setMilestones] = useState([])
  const [projectStatus, setProjectStatus] = useState(null)
  const [architecture, setArchitecture] = useState(null)

  const readOnly = !user || !EDITOR_ROLES.includes(user.role)

  const load = useCallback(async () => {
    if (!getToken()) return
    setLoading(true)
    try {
      const [data, ms, ps, arch] = await Promise.all([
        listItems(filters), listMilestones(), getProjectStatus(), getArchitecture(),
      ])
      setItems(data.items || [])
      setMilestones(ms.items || [])
      setProjectStatus(ps)
      setArchitecture(arch)
      setError('')
    } catch (e) {
      setError(e.message)
      if (e.status === 401) setUser(null)
    } finally {
      setLoading(false)
    }
  }, [filters])

  // Восстановление сессии: токен сохранён, профиль — нет, поэтому его
  // запрашиваем. Протухший или отозванный токен даёт 401, request() его
  // вычищает, и мы честно возвращаемся к форме входа.
  useEffect(() => {
    if (!booting) return
    let actual = true
    getMe()
      .then((me) => { if (actual) setUser(me) })
      .catch((e) => {
        if (!actual) return
        setSession(null)
        if (e.status !== 401) setError(e.message)
      })
      .finally(() => { if (actual) setBooting(false) })
    return () => { actual = false }
  }, [booting])

  useEffect(() => {
    if (user) load()
  }, [user, load])

  /** Перенос карточки в другой столбец. */
  async function move(item, status) {
    const blocker = transitionBlocker(item, status)
    if (blocker) {
      setError(`${item.id}: ${blocker}`)
      return
    }
    try {
      await setStatus(item.id, status)
      setError('')
      await load()
    } catch (e) {
      setError(e.missingStages ? `${e.message}: ${e.missingStages.join(', ')}` : e.message)
    }
  }

  /** Перестановка карточки в очереди исполнения. */
  async function reorder(id, afterId, beforeId) {
    try {
      await moveItem(id, afterId, beforeId)
      setError('')
      await load()
    } catch (e) {
      setError(e.message)
    }
  }

  /** Запись общего состояния проекта. */
  async function saveStatus(body) {
    try {
      await saveProjectStatus(body)
      setError('')
      await load()
    } catch (e) {
      setError(e.message)
    }
  }

  if (booting) {
    return (
      <div className="app">
        <div className="center"><p className="muted">Загружаем профиль…</p></div>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="app">
        {error && <div className="error" style={{ padding: '8px 18px' }}>{error}</div>}
        <LoginForm onSuccess={setUser} />
      </div>
    )
  }

  return (
    <div className="app">
      <header>
        <strong>Бэклог</strong>
        <div className="filters">
          <input type="search" placeholder="Поиск по id, названию, описанию"
                 value={filters.query}
                 onChange={(e) => setFilters({ ...filters, query: e.target.value })} />
          <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })}>
            <option value="">все типы</option>
            {TYPES.map((t) => <option key={t.key} value={t.key}>{t.title}</option>)}
          </select>
          <input placeholder="исполнитель" value={filters.assignee}
                 onChange={(e) => setFilters({ ...filters, assignee: e.target.value })} />
          <label className="muted" style={{ margin: 0, display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" style={{ width: 'auto' }} checked={filters.archived}
                   onChange={(e) => setFilters({ ...filters, archived: e.target.checked })} />
            архив
          </label>
          {loading && <span className="muted">обновляем…</span>}
        </div>
        <div className="row">
          <button className={`btn ghost${view === 'map' ? ' active' : ''}`}
                  onClick={() => setView('map')}>Этапы</button>
          <button className={`btn ghost${view === 'board' ? ' active' : ''}`}
                  onClick={() => setView('board')}>Доска</button>
          <button className={`btn ghost${view === 'arch' ? ' active' : ''}`}
                  onClick={() => setView('arch')}>Архитектура</button>
          {!readOnly && <button className="btn" onClick={() => setCreating(true)}>Новая карточка</button>}
          <span className="muted">{user.full_name || user.username} · {user.role}</span>
          <button className="btn ghost" onClick={() => { setSession(null); setUser(null); setError('') }}>Выйти</button>
        </div>
      </header>

      {error && <div className="error" style={{ padding: '8px 18px' }}>{error}</div>}
      {readOnly && (
        <div className="muted" style={{ padding: '6px 18px' }}>
          Роль {user.role}: доступен только просмотр
        </div>
      )}

      {view === 'arch' ? (
        <Architecture data={architecture} />
      ) : view === 'board' ? (
        <Board items={items} onOpen={setOpenId} onMove={move} readOnly={readOnly} />
      ) : (
        <StageMap milestones={milestones} items={items} status={projectStatus}
                  onOpen={setOpenId} onReorder={reorder} onSaveStatus={saveStatus}
                  readOnly={readOnly} />
      )}

      {openId && (
        <ItemPanel id={openId} readOnly={readOnly}
                   onClose={() => setOpenId(null)} onChanged={load} />
      )}
      {creating && (
        <CreateForm onCancel={() => setCreating(false)}
                    onCreated={() => { setCreating(false); load() }} />
      )}
      {/* Столбцы перечислены в модели; здесь используется только их порядок. */}
      {COLUMNS.length === 0 && <p className="muted">Модель статусов пуста</p>}
    </div>
  )
}
